import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, FileCheck2, ArrowLeft, Check, Calculator, PenLine } from 'lucide-react';
import DisclaimerBox from '../components/DisclaimerBox';
import ProductCard from '../components/ProductCard';
import CoaViewer from '../components/CoaViewer';
import { AddOns } from '../components/AddOns';
import { ReviewForm } from '../components/ReviewForm';
import { Stars } from '../components/Stars';
import { getProduct, getProducts, getReviews } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, openDrawer } = useCart();
  const { toast } = useToast();
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showCoa, setShowCoa] = useState(false);

  const loadReviews = () => getReviews(id).then(setReviews).catch(() => setReviews([]));

  useEffect(() => {
    setLoading(true);
    setQty(1);
    setShowReviewForm(false);
    loadReviews();
    let cancelled = false;
    const load = (attempt) => {
      getProduct(id)
        .then((p) => {
          if (cancelled) return;
          setProduct(p);
          setLoading(false);
          getProducts(p.category).then((list) => { if (!cancelled) setRelated(list.filter((x) => x.id !== id).slice(0, 4)); }).catch(() => {});
        })
        .catch((e) => {
          if (cancelled) return;
          if (e?.response?.status !== 404 && attempt < 3) {
            setTimeout(() => load(attempt + 1), 1500 * (attempt + 1));
            return;
          }
          setProduct(null);
          setLoading(false);
        });
    };
    load(0);
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const title = (product.seoTitle && product.seoTitle.trim()) || `${product.name}${product.size ? ' ' + product.size : ''} | Australian Luxe Peptides`;
    const desc = (product.seoDescription && product.seoDescription.trim()) || (product.description || `${product.name} — premium Australian research peptide. Research use only.`).slice(0, 300);
    const prevTitle = document.title;
    document.title = title;
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement('meta'); const [k, v] = sel.replace(/meta\[|\]/g, '').split('='); el.setAttribute(k, v.replace(/["']/g, '')); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', desc);
    if (product.image) setMeta('meta[property="og:image"]', 'content', product.image);

    const avg = reviews.length ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length) : 0;
    const ld = {
      '@context': 'https://schema.org', '@type': 'Product',
      name: product.name, description: desc,
      image: product.image ? [product.image] : undefined,
      sku: product.code || product.id, category: product.category,
      brand: { '@type': 'Brand', name: 'Australian Luxe Peptides' },
      offers: { '@type': 'Offer', price: Number(product.price || 0).toFixed(2), priceCurrency: 'AUD', availability: product.stockStatus === 'Out of Stock' ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock' },
    };
    if (reviews.length) ld.aggregateRating = { '@type': 'AggregateRating', ratingValue: avg.toFixed(1), reviewCount: reviews.length };
    let script = document.getElementById('product-jsonld');
    if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.id = 'product-jsonld'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(ld);

    return () => {
      document.title = prevTitle;
      const s = document.getElementById('product-jsonld');
      if (s) s.remove();
    };
  }, [product, reviews]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-5 py-32 text-center text-alp-gray">Loading&hellip;</div>;
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif text-4xl text-alp-cream mb-4">Product not found</h1>
        <Link to="/shop" className="text-alp-gold">Back to Shop</Link>
      </div>
    );
  }

  const out = product.stockStatus === 'Out of Stock';

  const handleAdd = () => {
    addToCart(product, qty);
    openDrawer();
  };

  return (
    <div className="bg-alp-black min-h-screen aurora-wash relative overflow-hidden">
      <div aria-hidden className="gold-orb w-80 h-80 -right-20 top-24 hidden md:block" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-alp-gray hover:text-alp-gold transition-colors mb-10">
          <ArrowLeft size={15} /> Back
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="relative aspect-square rounded-lg overflow-hidden border border-alp-gold/25 bg-alp-dark shadow-[0_0_45px_-12px_rgba(212,175,55,0.3)]">
            <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-alp-gold/60 to-transparent z-10" />
            <img src={product.image} alt={product.name} className={`w-full h-full object-cover ${out ? 'opacity-50 grayscale' : ''}`} />
            {(product.coaImages?.length > 0 || product.coa) && (
              <button
                type="button"
                data-testid="view-coa-btn"
                onClick={() => (product.coaImages?.length ? setShowCoa(true) : null)}
                disabled={!product.coaImages?.length}
                className="absolute top-4 right-4 flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full bg-alp-black/80 border border-alp-gold/40 text-alp-gold transition-colors enabled:hover:bg-alp-gold enabled:hover:text-alp-black disabled:cursor-default"
              >
                <FileCheck2 size={13} /> {product.coaImages?.length ? 'View COA' : 'COA Available'}
              </button>
            )}
          </div>

          <div className="glass-card rounded-lg p-7 md:p-9">
            <div className="text-[11px] tracking-[0.25em] uppercase text-alp-gray mb-3">{product.category}</div>
            <h1 className="font-serif text-5xl text-alp-cream leading-tight heading-glow">{product.name}</h1>
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              <span className="text-alp-gray">{product.code} &middot; {product.size}</span>
              <span className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1 rounded-full border ${
                product.stockStatus === 'In Stock' ? 'text-emerald-300/90 border-emerald-300/30' :
                product.stockStatus === 'Preorder' ? 'text-alp-gold border-alp-gold/40' :
                'text-alp-gray border-alp-gray/30'
              }`}>{product.stockStatus}</span>
              {product.ribbon ? (
                <span className="text-[11px] tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-alp-gold text-alp-black font-semibold">{product.ribbon}</span>
              ) : null}
            </div>

            <div className="w-16 h-px alp-beam my-7" />

            <div className="flex items-baseline gap-3 mb-7">
              <span className="text-4xl text-alp-gold">${product.price.toFixed(2)}</span>
              {product.compareAtPrice > product.price && (
                <span className="text-xl text-alp-gray/70 line-through">${product.compareAtPrice.toFixed(2)}</span>
              )}
              <span className="text-alp-gray text-base">AUD</span>
            </div>

            <p className="text-alp-gray leading-relaxed mb-8">{product.description}</p>

            {product.bundle && product.bundleItemsDetail?.length > 0 && (
              <div className="hairline rounded px-5 py-4 mb-8" data-testid="bundle-contents">
                <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-3">This bundle includes</p>
                <ul className="space-y-1.5">
                  {product.bundleItemsDetail.map((bi, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-alp-cream/85">
                      <Check size={14} className="text-alp-gold shrink-0" /> {bi.qty} &times; {bi.name}{bi.size ? ` (${bi.size})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.stockStatus === 'Preorder' && (
              <div className="hairline rounded px-5 py-4 mb-8 text-sm text-alp-cream/80">
                Preorder item. ETA usually 14&ndash;21 days after the weekly supplier order (cut-off Sunday).
              </div>
            )}

            {!out && (
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center border border-alp-line rounded">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-alp-cream hover:text-alp-gold transition-colors"><Minus size={16} /></button>
                  <span className="w-12 text-center text-alp-cream">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="p-3 text-alp-cream hover:text-alp-gold transition-colors"><Plus size={16} /></button>
                </div>
                <button onClick={handleAdd} data-testid="add-to-cart-btn" className="btn-glow flex-1 inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                  <Check size={16} /> Add to Cart
                </button>
              </div>
            )}
            {out && (
              <div className="mb-8 px-6 py-4 border border-alp-line rounded text-center text-alp-gray text-sm tracking-[0.15em] uppercase">
                Currently Out of Stock
              </div>
            )}

            <DisclaimerBox compact />

            {(() => {
              const mg = parseFloat(product.size) || '';
              const qname = encodeURIComponent(`${product.name}${product.size ? ' ' + product.size : ''}`);
              const nasalFriendly = /dsip|selank|semax|oxytocin|melanotan|mt-?2|mt-?ii|pt-?141|kisspeptin|thymosin beta|tb-?500/i.test(product.name || '');
              return (
                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <Link to={`/calculator?name=${qname}${mg ? `&peptideMg=${mg}` : ''}`} data-testid="product-calc-link" className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors">
                    <Calculator size={14} /> Reconstitution Calculator
                  </Link>
                  {nasalFriendly && (
                    <Link to={`/calculator?mode=nasal&name=${qname}${mg ? `&peptideMg=${mg}` : ''}`} data-testid="product-nasal-calc-link" className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors">
                    <Calculator size={14} /> Calculate Nasal Dose
                    </Link>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="mt-16 max-w-2xl">
          <AddOns title="Complete your order" excludeId={product.id} />
        </div>

        {/* Product reviews */}
        <div className="mt-16 max-w-3xl" data-testid="product-reviews">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-3xl text-alp-cream heading-glow">Reviews</h2>
              {reviews.length > 0 ? (
                <div className="flex items-center gap-2 mt-2">
                  <Stars value={Math.round(reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length)} />
                  <span className="text-alp-gray text-sm">{(reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length).toFixed(1)} &middot; {reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
                </div>
              ) : (
                <p className="text-alp-gray text-sm mt-2">No reviews yet for this product. Be the first.</p>
              )}
            </div>
            <button onClick={() => setShowReviewForm((v) => !v)} data-testid="product-write-review-btn" className="btn-glow inline-flex items-center gap-2 border border-alp-gold/50 text-alp-cream px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors">
              <PenLine size={14} /> {showReviewForm ? 'Close' : 'Write a Review'}
            </button>
          </div>

          {showReviewForm && (
            <div className="mb-8">
              <ReviewForm defaultProductId={product.id} defaultProductName={product.name} lockProduct onSubmitted={() => { setShowReviewForm(false); loadReviews(); }} />
            </div>
          )}

          {reviews.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reviews.map((r) => (
                <article key={r.id} data-testid={`product-review-${r.id}`} className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
                  <Stars value={r.rating} />
                  {r.body ? <p className="text-alp-cream/85 text-sm leading-relaxed mt-3">{r.body}</p> : null}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-alp-line/60">
                    <p className="text-alp-cream text-sm font-medium">{r.name}</p>
                    {r.date ? <p className="text-alp-gray text-xs">{r.date}</p> : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-3xl text-alp-cream mb-8">You May Also Consider</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
      {showCoa && product.coaImages?.length > 0 && (
        <CoaViewer images={product.coaImages} productName={product.name} onClose={() => setShowCoa(false)} />
      )}
    </div>
  );
}
